import { useState, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Image as ImageIcon,
  Trash2,
  Download,
  RefreshCw,
  HardDrive,
  FileImage,
  Eye,
  X,
  Loader2,
  Upload,
  AlertCircle,
  Copy,
  Check,
  FolderOpen,
  CheckCircle,
} from 'lucide-react';
import clsx from 'clsx';
import { getImageUrl } from '../../lib/supabase';
import {
  useMediaFiles,
  useMediaStats,
  useUploadMedia,
  useDeleteMedia,
  useReplaceMedia,
  useUpdateMediaAlt,
  formatFileSize,
  validateFile,
} from '../../services/mediaService';
import { debounce } from '../../lib/utils';

/* ── Slide-over animations ── */
const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const panelVariants = {
  hidden: { x: '100%' },
  visible: { x: 0, transition: { type: 'spring', damping: 30, stiffness: 300 } },
  exit: { x: '100%', transition: { duration: 0.2 } },
};

/**
 * Admin Media Library page.
 * - Page-wide drag & drop that auto-uploads on file drop
 * - Upload zone hidden until user clicks "Upload Image"
 * - Image details in a full-height slide-over panel
 * - Same UI as the rest of the admin
 */
export default function AdminMediaPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [viewImage, setViewImage] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showUploader, setShowUploader] = useState(false);

  // Page-level drag state
  const [pageDrag, setPageDrag] = useState(false);
  const dragCounter = useRef(0);

  // Upload state for page-level drop & uploader zone
  const [uploadFiles, setUploadFiles] = useState([]); // queued files being uploaded
  const [uploadingIds, setUploadingIds] = useState({}); // { fileName: progress% }

  const { data: files, isLoading, refetch } = useMediaFiles(debouncedSearch);
  const { data: stats } = useMediaStats();
  const uploadMutation = useUploadMedia();
  const deleteMutation = useDeleteMedia();
  const replaceMutation = useReplaceMedia();
  const altMutation = useUpdateMediaAlt();

  const handleSearchChange = useMemo(
    () => debounce((val) => setDebouncedSearch(val), 300),
    []
  );

  /* ── Page-level drag & drop ── */
  const handlePageDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) setPageDrag(true);
  };

  const handlePageDragLeave = (e) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setPageDrag(false);
  };

  const handlePageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handlePageDrop = useCallback(
    async (e) => {
      e.preventDefault();
      dragCounter.current = 0;
      setPageDrag(false);

      const droppedFiles = Array.from(e.dataTransfer.files || []);
      if (!droppedFiles.length) return;

      // Auto-upload each valid file
      for (const file of droppedFiles) {
        const check = validateFile(file);
        if (!check.valid) continue;

        setUploadingIds((prev) => ({ ...prev, [file.name]: 0 }));
        try {
          await uploadMutation.mutateAsync({
            file,
            onProgress: (pct) => {
              setUploadingIds((prev) => ({ ...prev, [file.name]: pct }));
            },
          });
        } catch {
          // swallow individual errors
        }
        setUploadingIds((prev) => {
          const next = { ...prev };
          delete next[file.name];
          return next;
        });
      }
    },
    [uploadMutation]
  );

  /* ── Uploader zone: file input + auto-upload ── */
  const uploaderRef = useRef(null);

  const handleUploaderFiles = useCallback(
    async (fileList) => {
      const fileArr = Array.from(fileList);
      for (const file of fileArr) {
        const check = validateFile(file);
        if (!check.valid) continue;
        setUploadingIds((prev) => ({ ...prev, [file.name]: 0 }));
        try {
          await uploadMutation.mutateAsync({
            file,
            onProgress: (pct) => {
              setUploadingIds((prev) => ({ ...prev, [file.name]: pct }));
            },
          });
        } catch {
          // swallow
        }
        setUploadingIds((prev) => {
          const next = { ...prev };
          delete next[file.name];
          return next;
        });
      }
    },
    [uploadMutation]
  );

  /* ── Replace handler ── */
  const handleReplace = useCallback(
    async (file) => {
      if (!replaceTarget) return;
      const check = validateFile(file);
      if (!check.valid) return;
      await replaceMutation.mutateAsync({
        oldRecord: replaceTarget,
        newFile: file,
      });
      setReplaceTarget(null);
    },
    [replaceTarget, replaceMutation]
  );

  /* ── Delete ── */
  const handleDelete = async (media, hardDelete = false) => {
    await deleteMutation.mutateAsync({
      id: media.id,
      filePath: media.file_path,
      hardDelete,
    });
    setDeleteConfirm(null);
    if (selectedMedia?.id === media.id) setSelectedMedia(null);
  };

  /* ── Copy URL ── */
  const handleCopyUrl = useCallback((filePath) => {
    const url = getImageUrl(filePath);
    if (url) {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }, []);

  /* ── Alt text update ── */
  const handleUpdateAlt = async (id, altText) => {
    await altMutation.mutateAsync({ id, altText });
  };

  const anyUploading = Object.keys(uploadingIds).length > 0;

  /* ── Stat cards ── */
  const statCards = [
    { label: 'Total Images', value: stats?.totalCount || 0, icon: FileImage, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Storage Used', value: formatFileSize(stats?.totalSize || 0), icon: HardDrive, color: 'text-amber-600', bg: 'bg-amber-50' },
    { label: 'File Types', value: Object.keys(stats?.byType || {}).length, icon: ImageIcon, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <div
      className="space-y-6 relative"
      onDragEnter={handlePageDragEnter}
      onDragLeave={handlePageDragLeave}
      onDragOver={handlePageDragOver}
      onDrop={handlePageDrop}
    >
      {/* ── Page-level drag overlay ── */}
      <AnimatePresence>
        {pageDrag && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-brand-red/10 backdrop-blur-sm flex items-center justify-center pointer-events-none"
          >
            <div className="bg-brand-white border-2 border-dashed border-brand-red rounded-2xl p-12 text-center shadow-2xl">
              <Upload size={48} className="text-brand-red mx-auto mb-4" />
              <p className="text-lg font-extrabold text-brand-dark">Drop to upload</p>
              <p className="text-sm text-brand-gray mt-1">Files will be uploaded automatically</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Media Library</h1>
          <p className="text-sm text-brand-gray mt-1">
            Manage all your uploaded images and media files.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowUploader((v) => !v)}
            className={clsx(
              'inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold rounded-xl transition-all',
              showUploader
                ? 'bg-brand-red text-brand-white'
                : 'bg-brand-red hover:bg-red-700 text-brand-white'
            )}
          >
            <Upload size={14} />
            Upload Image
          </button>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Stats row ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className="bg-brand-white border border-ui-border rounded-2xl p-5"
            >
              <div className="flex items-center gap-3">
                <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', card.bg)}>
                  <Icon size={20} className={card.color} />
                </div>
                <div>
                  <p className="text-xs font-medium text-brand-gray">{card.label}</p>
                  <p className="text-xl font-extrabold text-brand-dark">{card.value}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* ── Upload zone (hidden until button clicked) ── */}
      <AnimatePresence>
        {showUploader && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
          >
            <div className="bg-brand-white border border-ui-border rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-brand-dark">Upload Images</h3>
                <button
                  onClick={() => setShowUploader(false)}
                  className="p-1.5 rounded-lg text-brand-gray hover:bg-brand-light transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Drop zone */}
              <div
                onClick={() => uploaderRef.current?.click()}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleUploaderFiles(e.dataTransfer.files);
                }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="border-2 border-dashed border-ui-border hover:border-brand-red/40 rounded-2xl p-8 text-center cursor-pointer transition-all hover:bg-brand-light/50"
              >
                <input
                  ref={uploaderRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                  multiple
                  onChange={(e) => {
                    handleUploaderFiles(e.target.files);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                <div className="w-12 h-12 rounded-xl bg-brand-light flex items-center justify-center mx-auto mb-3">
                  <Upload size={22} className="text-brand-gray" />
                </div>
                <p className="text-sm font-bold text-brand-dark">
                  Drop images here or <span className="text-brand-red">browse</span>
                </p>
                <p className="text-xs text-brand-gray mt-1">JPG, PNG, WebP, GIF, SVG · Max 5 MB</p>
              </div>

              {/* Active uploads */}
              {anyUploading && (
                <div className="mt-4 space-y-2">
                  {Object.entries(uploadingIds).map(([name, pct]) => (
                    <div key={name} className="flex items-center gap-3 p-3 bg-brand-light rounded-xl">
                      <Loader2 size={14} className="animate-spin text-brand-red flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-brand-dark truncate">{name}</p>
                        <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            className="h-full bg-brand-red rounded-full"
                            transition={{ duration: 0.2 }}
                          />
                        </div>
                      </div>
                      <span className="text-[10px] font-bold text-brand-gray">{pct}%</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Uploads in progress (floating indicator when uploader is closed) ── */}
      {!showUploader && anyUploading && (
        <div className="bg-brand-white border border-ui-border rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Loader2 size={14} className="animate-spin text-brand-red" />
            <span className="text-xs font-bold text-brand-dark">
              Uploading {Object.keys(uploadingIds).length} file(s)…
            </span>
          </div>
          <div className="space-y-1">
            {Object.entries(uploadingIds).map(([name, pct]) => (
              <div key={name} className="flex items-center gap-2">
                <span className="text-[10px] text-brand-gray truncate flex-1">{name}</span>
                <div className="w-24 h-1 bg-brand-light rounded-full overflow-hidden">
                  <div className="h-full bg-brand-red rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-[10px] font-bold text-brand-gray w-8 text-right">{pct}%</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Search + Image Grid ── */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        className="bg-brand-white border border-ui-border rounded-2xl"
      >
        {/* Search bar */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-ui-border">
          <div className="relative flex-1 max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
            <input
              type="text"
              placeholder="Search by file name or alt text…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                handleSearchChange(e.target.value);
              }}
              className="w-full pl-8 pr-4 py-2 text-sm bg-brand-light border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
            />
          </div>
          <span className="text-xs font-medium text-brand-gray">
            {files?.length || 0} images
          </span>
        </div>

        {/* Grid */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={28} className="animate-spin text-brand-red" />
            </div>
          ) : !files?.length ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-2xl bg-brand-light flex items-center justify-center mb-4">
                <HardDrive size={28} className="text-ui-muted" />
              </div>
              <p className="text-sm font-bold text-brand-dark">No images found</p>
              <p className="text-xs text-brand-gray mt-1">
                {search ? 'Try a different search term' : 'Drop files anywhere or click Upload Image'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {files.map((file) => {
                const url = getImageUrl(file.file_path);
                const isSelected = selectedMedia?.id === file.id;
                return (
                  <motion.div
                    key={file.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className={clsx(
                      'relative group rounded-xl overflow-hidden border-2 aspect-square cursor-pointer transition-all',
                      isSelected
                        ? 'border-brand-red ring-2 ring-brand-red/20'
                        : 'border-ui-border hover:border-brand-red/40'
                    )}
                    onClick={() => setSelectedMedia(isSelected ? null : file)}
                  >
                    {url ? (
                      <img src={url} alt={file.alt_text || file.file_name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-brand-light flex items-center justify-center">
                        <ImageIcon size={24} className="text-ui-muted" />
                      </div>
                    )}

                    {isSelected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-brand-red rounded-full flex items-center justify-center shadow-md">
                        <Check size={14} className="text-brand-white" />
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/40 transition-colors">
                      <div className="absolute bottom-0 inset-x-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-[10px] font-medium text-brand-white truncate">{file.file_name}</p>
                        <p className="text-[9px] text-brand-white/70">{formatFileSize(file.file_size)}</p>
                      </div>
                      <div className="absolute top-2 left-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => { e.stopPropagation(); setViewImage(file); }}
                          className="w-7 h-7 bg-brand-white/90 rounded-lg flex items-center justify-center hover:bg-brand-white transition-colors"
                          title="Preview"
                        >
                          <Eye size={12} className="text-brand-dark" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleCopyUrl(file.file_path); }}
                          className="w-7 h-7 bg-brand-white/90 rounded-lg flex items-center justify-center hover:bg-brand-white transition-colors"
                          title="Copy URL"
                        >
                          {copied ? <Check size={12} className="text-success" /> : <Copy size={12} className="text-brand-dark" />}
                        </button>
                      </div>
                    </div>

                    {file.products?.title && (
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 text-[8px] font-bold bg-brand-dark/60 text-brand-white rounded-md backdrop-blur-sm max-w-[80px] truncate">
                        {file.products.title}
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════════
       * IMAGE DETAILS — Full-height slide-over panel
       * ═══════════════════════════════════════════════ */}
      <AnimatePresence>
        {selectedMedia && (
          <>
            <motion.div
              key="media-detail-backdrop"
              variants={overlayVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={() => setSelectedMedia(null)}
              className="fixed inset-0 z-40 bg-brand-dark/40 backdrop-blur-sm"
            />
            <motion.div
              key="media-detail-panel"
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-brand-white shadow-2xl flex flex-col"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-ui-border flex-shrink-0">
                <div>
                  <h2 className="text-lg font-extrabold text-brand-dark">Image Details</h2>
                  <p className="text-xs text-brand-gray mt-0.5 truncate max-w-[280px]">
                    {selectedMedia.file_name}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedMedia(null)}
                  className="p-2 rounded-lg text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Preview */}
                <div className="aspect-video rounded-xl overflow-hidden bg-brand-light border border-ui-border">
                  {getImageUrl(selectedMedia.file_path) ? (
                    <img
                      src={getImageUrl(selectedMedia.file_path)}
                      alt={selectedMedia.alt_text || selectedMedia.file_name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon size={40} className="text-ui-muted" />
                    </div>
                  )}
                </div>

                {/* File meta grid */}
                <div className="grid grid-cols-2 gap-4">
                  <InfoRow label="File Name" value={selectedMedia.file_name} />
                  <InfoRow label="Size" value={formatFileSize(selectedMedia.file_size)} />
                  <InfoRow label="Type" value={selectedMedia.mime_type} />
                  <InfoRow
                    label="Dimensions"
                    value={selectedMedia.width ? `${selectedMedia.width} × ${selectedMedia.height}` : '—'}
                  />
                  <InfoRow
                    label="Uploaded"
                    value={new Date(selectedMedia.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'short', day: 'numeric',
                      hour: '2-digit', minute: '2-digit',
                    })}
                  />
                  <InfoRow label="Linked Product" value={selectedMedia.products?.title || 'None'} />
                </div>

                {/* Alt text */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">Alt Text</label>
                  <input
                    type="text"
                    defaultValue={selectedMedia.alt_text || ''}
                    onBlur={(e) => {
                      if (e.target.value !== (selectedMedia.alt_text || '')) {
                        handleUpdateAlt(selectedMedia.id, e.target.value);
                      }
                    }}
                    placeholder="Describe this image…"
                    className="w-full px-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  />
                </div>

                {/* Public URL */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">Public URL</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={getImageUrl(selectedMedia.file_path) || ''}
                      className="flex-1 px-3 py-2 text-xs bg-brand-light border-0 rounded-xl text-brand-gray font-mono truncate"
                    />
                    <button
                      onClick={() => handleCopyUrl(selectedMedia.file_path)}
                      className="flex items-center gap-1 px-3 py-2 text-xs font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl transition-colors flex-shrink-0"
                    >
                      {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Footer actions */}
              <div className="px-6 py-4 border-t border-ui-border flex items-center gap-2 bg-brand-white flex-shrink-0">
                <button
                  onClick={() => setViewImage(selectedMedia)}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold text-brand-dark bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  <Eye size={12} /> Preview
                </button>
                <button
                  onClick={() => setReplaceTarget(selectedMedia)}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors"
                >
                  <RefreshCw size={12} /> Replace
                </button>
                <button
                  onClick={() => {
                    const url = getImageUrl(selectedMedia.file_path);
                    if (url) window.open(url, '_blank');
                  }}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold text-green-600 bg-green-50 hover:bg-green-100 rounded-xl transition-colors"
                >
                  <Download size={12} /> Open
                </button>
                <button
                  onClick={() => setDeleteConfirm(selectedMedia)}
                  className="flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold text-error bg-red-50 hover:bg-red-100 rounded-xl transition-colors ml-auto"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════
       * Full preview modal
       * ═══════════════════════════════════ */}
      <AnimatePresence>
        {viewImage && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setViewImage(null)}
              className="fixed inset-0 z-[60] bg-brand-dark/80 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="fixed inset-8 z-[60] flex items-center justify-center"
            >
              <button
                onClick={() => setViewImage(null)}
                className="absolute top-0 right-0 p-3 text-brand-white hover:text-brand-light transition-colors"
              >
                <X size={24} />
              </button>
              <img
                src={getImageUrl(viewImage.file_path)}
                alt={viewImage.alt_text || viewImage.file_name}
                className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
              />
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-brand-dark/80 text-brand-white px-4 py-2 rounded-xl text-xs font-medium backdrop-blur-sm">
                {viewImage.file_name} · {formatFileSize(viewImage.file_size)}
                {viewImage.width && ` · ${viewImage.width}×${viewImage.height}`}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════
       * Delete confirmation
       * ═══════════════════════════════════ */}
      <AnimatePresence>
        {deleteConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setDeleteConfirm(null)}
              className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-sm bg-brand-white rounded-2xl shadow-2xl p-6"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={20} className="text-error" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-brand-dark">Delete Image</h3>
                  <p className="text-xs text-brand-gray mt-1">
                    Are you sure you want to delete <strong>{deleteConfirm.file_name}</strong>?
                  </p>
                  {deleteConfirm.products?.title && (
                    <p className="text-xs text-warning mt-2 flex items-center gap-1">
                      <AlertCircle size={12} />
                      Used by product: {deleteConfirm.products.title}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleDelete(deleteConfirm, false)}
                  disabled={deleteMutation.isPending}
                  className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold text-error bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                >
                  {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Soft Delete
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm, true)}
                  disabled={deleteMutation.isPending}
                  className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-bold text-brand-white bg-error hover:bg-red-700 rounded-xl transition-colors"
                >
                  Permanent
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════
       * Replace file modal
       * ═══════════════════════════════════ */}
      <AnimatePresence>
        {replaceTarget && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setReplaceTarget(null)}
              className="fixed inset-0 z-[60] bg-brand-dark/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[60] w-full max-w-md bg-brand-white rounded-2xl shadow-2xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-bold text-brand-dark">Replace Image</h3>
                  <p className="text-xs text-brand-gray mt-0.5">Replacing: {replaceTarget.file_name}</p>
                </div>
                <button
                  onClick={() => setReplaceTarget(null)}
                  className="p-1.5 rounded-lg text-brand-gray hover:bg-brand-light transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex items-center gap-4 mb-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-brand-light border border-ui-border flex-shrink-0">
                  <img
                    src={getImageUrl(replaceTarget.file_path)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-bold text-brand-dark">Current image</p>
                  <p className="text-xs text-brand-gray">{formatFileSize(replaceTarget.file_size)}</p>
                </div>
              </div>

              <label className="block border-2 border-dashed border-ui-border hover:border-brand-red/40 rounded-xl p-6 text-center cursor-pointer transition-all hover:bg-brand-light/50">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleReplace(file);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                <Upload size={24} className="text-brand-gray mx-auto mb-2" />
                <p className="text-sm font-bold text-brand-dark">
                  Click to choose replacement
                </p>
                <p className="text-xs text-brand-gray mt-1">Max 5 MB · JPG, PNG, WebP, GIF, SVG</p>
              </label>

              {replaceMutation.isPending && (
                <div className="flex items-center gap-2 mt-4 p-3 bg-brand-light rounded-xl">
                  <Loader2 size={14} className="animate-spin text-brand-red" />
                  <span className="text-xs font-medium text-brand-gray">Replacing…</span>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ── Small info row ── */
function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-semibold text-brand-gray uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-brand-dark truncate mt-0.5">{value}</p>
    </div>
  );
}
